import React from "react";
import { Box, Typography, Stack, Checkbox } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { EmailInput, PasswordInput } from "../UIElements/inputs";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "../../validate/authentication";

import { PATH_AUTH, PATH_DASHBOARD } from "../../routes/path";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import useResponsive from "../../hooks/useResponsive";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiSignIn } from "../../api/auth";
import { useNotificationService } from "../notification";
import { useAuth } from "../../hooks/useAuth";

//
const LoginDetails: React.FC = () => {
  const theme = useTheme();
  const { set_data, set_cookie_token } = useAuth();
  const isMedium = useResponsive("down", "md", "xs");
  const navigate = useNavigate();
  const { showNotification } = useNotificationService();

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(loginSchema),
  });
  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiSignIn,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Login successful", "success");
        const userData = {
          _id: data?.data?.data?._id ?? "",
          name: data?.data?.data?.name ?? "",
          email: data?.data?.data?.email ?? "",
          role: data?.data?.data?.role ?? "",
          type: data?.data?.data?.type ?? "",
          level: data?.data?.data?.level ?? 0,
          facilityId: data?.data?.data?.facilityId ?? "",
        };

        set_cookie_token(data?.data?.data);
        set_data(userData);
        navigate(PATH_DASHBOARD.root);
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });
  const onSubmitHandler = async (values: any): Promise<void> => {
    const { email, password } = values;

    const data = { password: password, email: email };
    mutation.mutate(data);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <EmailInput
          labelText="Email"
          errors={errors}
          placeholder="Enter email"
          register={register}
        />
        <PasswordInput
          labelText="Password"
          errors={errors}
          placeholder="Enter password"
          register={register}
        />
        <Stack direction="row" alignItems={"center"}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "Lexend",
                fontSize: "15px",
                fontWeight: 400,
                lineHeight: "20px",
                letterSpacing: "-0.23999999463558197px",
              }}
            >
              Remember me
            </Typography>
          </Box>
          <Link
            style={{
              color: theme.palette.text.secondary,
              marginLeft: "auto",
              textDecorationLine: "none",
            }}
            to={PATH_AUTH.resetPassword}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Lexend",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "18x",
              }}
            >
              Forgot Password?
            </Typography>
          </Link>
        </Stack>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3 }}
        >
          <LoadingButton
            loading={mutation.isPending}
            type="submit"
            variant="contained"
            sx={{
              width: "100%",
              borderRadius: "12px",
              fontFamily: "DM Sans",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "19.84px",
            }}
          >
            Sign in
          </LoadingButton>
        </Stack>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Lexend",
              fontSize: "15px",
              fontWeight: 400,
              lineHeight: "20px",
              letterSpacing: "-0.23999999463558197px",
            }}
          >
            Don't have an account?
          </Typography>
          <Link
            style={{
              color: theme.palette.common.black,
              textDecorationLine: "none",
            }}
            to={PATH_AUTH.signUp}
          >
            <Typography
              variant="body2"
              color={"primary"}
              sx={{
                fontFamily: "Lexend",
                fontSize: "15px",
                fontWeight: 700,
                lineHeight: "20px",
                letterSpacing: "-0.23999999463558197px",
              }}
            >
              Sign up
            </Typography>
          </Link>
        </Stack>
      </Box>
    </Box>
  );
};

export default LoginDetails;
